import { Megaphone, RefreshCw, TriangleAlert } from 'lucide-react';
import { getApiError, useGetMyNoticesQuery, type ParentNotice } from '@/store/api';
import { Mark } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageLoader } from '@/components/ui/Spinner';
import { relativeDay } from '@/lib/datetime';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const stampFmt = new Intl.DateTimeFormat('en-GH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/*
  Notices, as a parent sees them.

  ── Why urgent ones are pinned rather than just coloured ───────────────────

  A list ordered strictly by time puts "school closed tomorrow, burst pipe"
  underneath "swimming kit on Thursday" the moment anything else is posted. The
  school marked one of those urgent; honouring that in the ORDER is the only
  version that survives a busy week.

  ── Why the whole body is shown ────────────────────────────────────────────

  These are short announcements, and a notice truncated to "School closes on
  Friday 24th and reop…" with a tap target is a notice half the school misreads.
*/
export function ParentNoticesPage() {
  const { data: notices = [], isLoading, isError, error, isFetching, refetch } = useGetMyNoticesQuery();

  if (isLoading) return <PageLoader label="Loading notices…" />;

  /* Urgent first, then newest. The API already sorts by date. */
  const ordered = [...notices].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority === 'urgent' ? -1 : 1;
    return new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime();
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className={styles.pageTitle}>Notices</h1>
          <p className={cn(styles.meta, 'mt-0.5')}>Announcements from the school.</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Refresh"
          icon={<RefreshCw size={14} className={isFetching ? 'animate-spin' : undefined} />}
          onClick={() => void refetch()}
        />
      </div>

      {isError ? (
        <div className={cn(styles.panel, 'overflow-clip')}>
          <EmptyState
            icon={<TriangleAlert size={17} />}
            title="Could not load notices"
            description={getApiError(error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        </div>
      ) : ordered.length === 0 ? (
        <div className={cn(styles.panel, 'overflow-clip')}>
          <EmptyState
            icon={<Megaphone size={17} />}
            title="No notices"
            description="Announcements from the school appear here."
          />
        </div>
      ) : (
        ordered.map((notice) => <NoticeCard key={notice._id} notice={notice} />)
      )}
    </div>
  );
}

function NoticeCard({ notice }: { notice: ParentNotice }) {
  const urgent = notice.priority === 'urgent';

  return (
    <div className={cn(styles.panel, urgent && 'border-destructive/40')}>
      <div className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-[13px] font-bold text-foreground">{notice.title}</h2>
          {urgent && <Mark tone="danger">Urgent</Mark>}
        </div>

        <p className="mt-1.5 whitespace-pre-line text-[12px] leading-relaxed text-muted-foreground">
          {notice.body}
        </p>

        <p className={cn(styles.meta, 'mt-2')}>
          {stampFmt.format(new Date(notice.publishedAt))}
          <span className="opacity-70"> · {relativeDay(notice.publishedAt)}</span>
        </p>
      </div>
    </div>
  );
}
